import React, { useState, useEffect } from 'react';
import { X, TrendingUp, Target, Zap } from 'lucide-react';
import { Button } from '../ds/Button';
import { InteractiveTrajectory } from './InteractiveTrajectory';

/**
 * CoachingModal - AI coaching tips + interactive simulator
 *
 * Left: 3 ranked tips
 * Center: Side-view trajectory (current vs simulated)
 * Right: Preset buttons + sliders
 */
export const CoachingModal = ({ shotGroups, onClose }) => {
  const [tips, setTips] = useState([]);
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [simulatedParams, setSimulatedParams] = useState(null);

  // Fetch coaching tips on mount
  useEffect(() => {
    const fetchTips = async () => {
      try {
        const allShots = shotGroups.flatMap(g =>
          g.shots.filter(s => s.included).map(s => ({
            ball_speed: s.metrics.ballSpeed,
            launch_angle: s.metrics.launchAngle,
            spin_rate: s.metrics.spinRate,
            spin_axis: s.metrics.spinAxis,
            attack_angle: s.metrics.attackAngle,
            carry_distance: s.metrics.carry,
            offline_distance: s.metrics.offlineDistance
          }))
        );

        const response = await fetch('/api/coach/analyze', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ shots: allShots, goal: 'distance' })
        });

        if (!response.ok) throw new Error('Coach analysis failed');

        const data = await response.json();
        setTips(data.tips);
        setPresets(data.presets);
        setSimulatedParams(data.presets.current);  // Start with current values
      } catch (error) {
        console.error('Failed to fetch coaching tips:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTips();
  }, [shotGroups]);

  const applyPreset = (presetName) => {
    setSimulatedParams(presets[presetName]);
  };

  const updateMetric = (metric, value) => {
    setSimulatedParams(prev => ({
      ...prev,
      [metric]: parseFloat(value)
    }));
  };

  const rankIcons = {
    1: '🥇',
    2: '🥈',
    3: '🥉'
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-lg w-full max-w-7xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Zap className="text-yellow-400" size={24} />
            <h2 className="text-xl font-bold text-white">Performance Insights & Simulator</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-hover rounded transition-colors"
          >
            <X size={20} className="text-zinc-400" />
          </button>
        </div>

        {/* Main Content - 3 Panels */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - AI Tips (30%) */}
          <div className="w-[30%] border-r border-border p-4 overflow-y-auto">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Target size={20} />
              Your Improvement Plan
            </h3>

            {loading ? (
              <div className="text-zinc-400 text-center py-8">Analyzing...</div>
            ) : tips.length === 0 ? (
              <div className="text-zinc-400 text-center py-8">
                Your metrics look solid! Keep practicing consistency.
              </div>
            ) : (
              <div className="space-y-4">
                {tips.map((tip, idx) => (
                  <div
                    key={idx}
                    className="bg-surface-hover border border-border rounded-lg p-4"
                  >
                    <div className="flex items-start gap-3 mb-2">
                      <span className="text-2xl">{rankIcons[tip.rank]}</span>
                      <div className="flex-1">
                        <div className="text-xs text-zinc-400 uppercase mb-1">
                          {tip.rank === 1 ? 'BIGGEST IMPACT' : tip.rank === 2 ? 'Good Gain' : 'Fine-tune'}
                        </div>
                        <div className="text-sm font-semibold text-white mb-1">
                          {tip.message}
                        </div>
                        <div className="text-xs text-emerald-400">
                          Est: {tip.impact}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => applyPreset(tip.metric === 'spin_axis' ? 'straighter' : 'distance')}
                      className="w-full mt-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                    >
                      Try This Preset
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Center Panel - Trajectory (50%) */}
          <div className="flex-1 p-4">
            <h3 className="text-sm font-semibold text-white mb-2">
              Side View Trajectory
            </h3>
            <p className="text-xs text-zinc-400 mb-4">
              Blue = Your current average | Green = Simulated with adjustments
            </p>

            <div className="h-[calc(100%-4rem)]">
              <InteractiveTrajectory
                currentShots={shotGroups.flatMap(g => g.shots.filter(s => s.included).map(s => s.metrics))}
                simulatedParams={simulatedParams}
              />
            </div>
          </div>

          {/* Right Panel - Controls (20%) */}
          <div className="w-[20%] border-l border-border p-4 overflow-y-auto">
            <h3 className="text-sm font-semibold text-white mb-4">Quick Presets</h3>

            <div className="space-y-2 mb-6">
              <button
                onClick={() => applyPreset('straighter')}
                className="w-full px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded transition-colors flex items-center justify-center gap-2"
              >
                🎯 Straighter
              </button>

              <button
                onClick={() => applyPreset('distance')}
                className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors flex items-center justify-center gap-2"
              >
                ⬆️ More Distance
              </button>

              <button
                onClick={() => applyPreset('current')}
                className="w-full px-3 py-2 bg-zinc-600 hover:bg-zinc-700 text-white text-sm rounded transition-colors flex items-center justify-center gap-2"
              >
                ↺ Reset
              </button>
            </div>

            <h3 className="text-sm font-semibold text-white mb-4">Custom Adjust</h3>

            {simulatedParams && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-zinc-400 block mb-1">
                    Launch: {simulatedParams.launch_angle?.toFixed(1)}°
                  </label>
                  <input
                    type="range"
                    min="8"
                    max="18"
                    step="0.5"
                    value={simulatedParams.launch_angle || 12}
                    onChange={(e) => updateMetric('launch_angle', e.target.value)}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="text-xs text-zinc-400 block mb-1">
                    Spin Axis: {simulatedParams.spin_axis?.toFixed(1)}°
                  </label>
                  <input
                    type="range"
                    min="-5"
                    max="5"
                    step="0.1"
                    value={simulatedParams.spin_axis || 0}
                    onChange={(e) => updateMetric('spin_axis', e.target.value)}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="text-xs text-zinc-400 block mb-1">
                    Spin Rate: {simulatedParams.spin_rate?.toFixed(0)} rpm
                  </label>
                  <input
                    type="range"
                    min="1500"
                    max="4000"
                    step="100"
                    value={simulatedParams.spin_rate || 2800}
                    onChange={(e) => updateMetric('spin_rate', e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>
            )}

            <div className="mt-6">
              <Button
                variant="primary"
                onClick={onClose}
                className="w-full"
              >
                Apply & Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
